import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { setStoredUser } from "../lib/auth";
import { EDUCATION_LEVEL_OPTIONS, GENDER_OPTIONS, GOVERNORATE_OPTIONS } from "../lib/formOptions";
import brandFullTransparent from "../assets/brand-full-transparent.png";
import { useLanguage } from "../lib/language";

function Register() {
  const navigate = useNavigate();
  const { language, toggleLanguage, t } = useLanguage();
  const isArabic = language === "ar";
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    password: "",
    role: "student",
    birth_date: "",
    age: "",
    national_id: "",
    governorate: "",
    phone_number: "",
    gender: "male",
    education_level: "",
    institution_name: "",
    accepted_terms: false,
    remember_me: true
  });
  const [message, setMessage] = useState("");

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    const nextValue = type === "checkbox" ? checked : value;
    const nextFormData = {
      ...formData,
      [name]: nextValue
    };

    if (name === "birth_date" && value) {
      const today = new Date();
      const birthDate = new Date(value);
      let nextAge = today.getFullYear() - birthDate.getFullYear();
      const monthDelta = today.getMonth() - birthDate.getMonth();
      if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < birthDate.getDate())) {
        nextAge -= 1;
      }
      nextFormData.age = nextAge >= 0 ? String(nextAge) : "";
    }

    if (name === "national_id") {
      nextFormData.national_id = String(nextValue).replace(/\D/g, "").slice(0, 14);
    }

    setFormData(nextFormData);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");

    if (!/^\d{14}$/.test(formData.national_id)) {
      setMessage(t("invalidNationalId"));
      return;
    }

    if (!/^\d+$/.test(String(formData.age || ""))) {
      setMessage(t("invalidAge"));
      return;
    }

    if (!formData.accepted_terms) {
      setMessage(t("termsRequired"));
      return;
    }

    try {
      await api.post("/register", {
        ...formData,
        accepted_terms: formData.accepted_terms
      });

      if (formData.role === "trainer") {
        setMessage(
          isArabic
            ? "تم إنشاء حساب المدرب، لكنه يحتاج موافقة الأدمن قبل تسجيل الدخول."
            : "The trainer account was created, but it needs admin approval before sign-in."
        );
        window.setTimeout(() => navigate("/"), 1400);
        return;
      }

      const loginResponse = await api.post("/login", {
        email: formData.email,
        password: formData.password
      });
      setStoredUser(loginResponse.data.user, { remember: formData.remember_me });
      navigate(loginResponse.data.user.role === "trainer" ? "/dashboard" : "/exam");
    } catch (error) {
      setMessage(error.response?.data?.error || t("serverError"));
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-hero">
        <div className="auth-toolbar">
          <img src={brandFullTransparent} alt={t("brandTitle")} className="auth-brand-image" />
          <button className="language-toggle language-toggle--ghost" onClick={toggleLanguage} type="button">
            {language === "ar" ? t("switchToEnglish") : t("switchToArabic")}
          </button>
        </div>

        <h1>{t("registerHeroTitle")}</h1>
        <p className="auth-hero__text">{t("registerHeroText")}</p>
      </div>

      <div className="auth-card auth-card--wide">
        <h2 className="auth-card__title">{t("registerTitle")}</h2>

        <form onSubmit={handleSubmit} className="auth-form-grid">
          <input name="full_name" placeholder={t("fullName")} onChange={handleChange} className="auth-input" value={formData.full_name} />
          <input name="email" type="email" placeholder={t("email")} onChange={handleChange} className="auth-input" value={formData.email} />
          <input name="password" type="password" placeholder={t("password")} onChange={handleChange} className="auth-input" value={formData.password} />

          <select name="role" onChange={handleChange} className="auth-input" value={formData.role}>
            <option value="student">{t("student")}</option>
            <option value="trainer">{t("trainer")}</option>
          </select>

          <input name="birth_date" type="date" onChange={handleChange} className="auth-input" value={formData.birth_date} />
          <input name="age" type="number" min="1" placeholder={t("age")} onChange={handleChange} className="auth-input" value={formData.age} />

          <div className="field-block">
            <input
              name="national_id"
              placeholder={t("nationalId")}
              onChange={handleChange}
              className="auth-input"
              value={formData.national_id}
              inputMode="numeric"
              maxLength={14}
            />
            <small className="muted-text">{t("nationalIdHint")}</small>
          </div>

          <select name="governorate" onChange={handleChange} className="auth-input" value={formData.governorate}>
            <option value="">{t("selectGovernorate")}</option>
            {GOVERNORATE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {isArabic ? option.labelAr : option.labelEn}
              </option>
            ))}
          </select>

          <input name="phone_number" placeholder={t("phoneNumber")} onChange={handleChange} className="auth-input" value={formData.phone_number} />

          <select name="education_level" onChange={handleChange} className="auth-input" value={formData.education_level}>
            <option value="">{t("selectEducationLevel")}</option>
            {EDUCATION_LEVEL_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {isArabic ? option.labelAr : option.labelEn}
              </option>
            ))}
          </select>

          <input
            name="institution_name"
            placeholder={t("institutionName")}
            onChange={handleChange}
            className="auth-input"
            value={formData.institution_name}
          />

          <select name="gender" onChange={handleChange} className="auth-input" value={formData.gender}>
            {GENDER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {isArabic ? option.labelAr : option.labelEn}
              </option>
            ))}
          </select>

          <label className="auth-checkbox auth-checkbox--full">
            <input
              type="checkbox"
              name="remember_me"
              checked={formData.remember_me}
              onChange={handleChange}
            />
            <span>{t("keepSignedInAfterRegister")}</span>
          </label>

          <label className="auth-checkbox auth-checkbox--full">
            <input
              type="checkbox"
              name="accepted_terms"
              checked={formData.accepted_terms}
              onChange={handleChange}
            />
            <span>
              {isArabic ? "أوافق على " : "I agree to the "}
              <Link to="/terms" className="inline-link">
                {isArabic ? "الشروط والأحكام" : "terms and conditions"}
              </Link>
              {isArabic ? " وسياسة الخصوصية." : " and privacy policy."}
            </span>
          </label>

          <button type="submit" className="primary-button auth-button auth-button--full">
            {t("registerButton")}
          </button>
        </form>

        {message && <p className="auth-message">{message}</p>}

        <p className="auth-link-text">
          {t("haveAccount")} <Link to="/">{t("loginLink")}</Link>
        </p>
      </div>
    </div>
  );
}

export default Register;
